import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { format } from 'date-fns';

import { TOpportunity } from 'lib/models/opportunity';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { TouchpointType } from 'lib/models/touchpoint';
import { EventTouchpointable } from 'lib/models/event';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import styles from './OpportunityCardLink.module.scss';

export const OpportunityCardLink = ({ opportunity }: { opportunity: TOpportunity }) => {
  const [tc] = useTranslation('common');
  const { title, slug, touchpointable_type, public_url, is_online, place: placeResponse, cities: citiesResponse, touchpointable } = opportunity;
  const isEvent = touchpointable_type === TouchpointType.Event;
  const url = getOpportunityPublicPagePathname(touchpointable_type, slug) || public_url;

  const eventStartTime = useMemo(() => {
    if (!isEvent) return null;
    const eventTouchpointable = parseResponse(touchpointable as EventTouchpointable);
    return format(new Date(eventTouchpointable.start_date), 'MMM d, hh:mma');
  }, [isEvent, touchpointable]);

  const location = useMemo(() => {
    if (is_online) return isEvent ? tc('virtual') : tc('remote');

    const cities = parseArrayResponse(citiesResponse);
    const place = parseResponse(placeResponse);
    const citiesList = cities.map((city) => city.name).join(', ');
    return citiesList ? citiesList : place?.city || place?.country;
  }, [isEvent, is_online, placeResponse, citiesResponse]);

  return (
    <a href={url} rel="noreferrer" target="_blank">
      <li className={styles['container']}>
        {eventStartTime && <p className={styles['event-time']}>{eventStartTime}</p>}
        <h3 className={styles['title']}>{title}</h3>
        <p className={styles['type-location']}>
          <span>{touchpointable_type}</span>
          {location && (
            <>
              <div className={styles['separator-container']}>
                <span className={styles.separator}></span>
              </div>
              <span>{location}</span>
            </>
          )}
        </p>
      </li>
    </a>
  );
};

OpportunityCardLink.displayName = 'OpportunityCardLink';
